import { Tools } from './helpers/Tools'
import { BoundingRect, Coordinates } from './helpers/Types'
import { View } from './helpers/View'
import { Schema } from './Schema'

/**
 * SchemaObject class
 * @author Nikitchenko Sergey <nikitchenko.sergey@yandex.ru>
 */
export class SchemaObject {
  /**
   * Object unique id
   */
  public id: number

  /**
   * X position
   */
  public x: number

  /**
   * Y position
   */
  public y: number

  /**
   * Width
   */
  public width: number

  /**
   * Height
   */
  public height: number

  /**
   * Is active
   */
  public active = true

  /**
   * Rotation
   */
  public rotation = 0

  /**
   * Is hovered
   */
  public isHovered = false

  /**
   * Cursor style
   */
  public cursorStyle = 'pointer'

  /**
   * Render function
   */
  public renderFunction(schemaObject: SchemaObject, schemaDesigner: Schema, view: View) {
    if (this.active) {
      return this.placeRenderer(schemaObject, schemaDesigner, view)
    } else {
      return this.labelRenderer(schemaObject, schemaDesigner, view)
    }
  }

  /**
   * Place render function
   */
  public placeRenderer(_schemaObject: SchemaObject, _schemaDesigner: Schema, _view: View): void {}

  public setPlaceRender(
    value: (schemaObject: SchemaObject, schemaDesigner: Schema, view: View) => void
  ): void {
    this.placeRenderer = value
  }

  /**
   * Label render function
   */
  public labelRenderer(_schemaObject: SchemaObject, _schemaDesigner: Schema, _view: View): void {}

  public setLabelRender(
    value: (schemaObject: SchemaObject, schemaDesigner: Schema, view: View) => void
  ): void {
    this.labelRenderer = value
  }

  /**
   * Click function
   */
  public clickFunction(
    schemaObject: SchemaObject,
    _schemaDesigner: Schema,
    _view: View,
    _e: Event
  ) {
    const objectParams = schemaObject.getParams()
    objectParams.isSelected = !objectParams.isSelected
  }

  /**
   * Mouse over function
   */
  public mouseOverFunction: Function

  /**
   * Mouse leave function
   */
  public mouseLeaveFunction: Function

  /**
   * Check point in object
   */
  public pointInObjectFunction(
    schemaObject: SchemaObject,
    point: Coordinates,
    _schemaDesigner: Schema,
    _view: View
  ) {
    if (!this.active) {
      return
    }
    const width = schemaObject.getWidth()

    const halfWidth = width / 2
    return Math.sqrt((point.x - halfWidth) ** 2 + (point.y - halfWidth) ** 2) < halfWidth
  }

  /**
   * Clear function
   */
  public clearFunction(schemaObject: SchemaObject, _schemaDesigner: Schema, view: View) {
    const context = view.getContext()

    const borderWidth = 5
    context.clearRect(
      schemaObject.x - borderWidth,
      schemaObject.y - borderWidth,
      this.width + borderWidth * 2,
      this.height + borderWidth * 2
    )
  }

  /**
   * All params of object
   */
  public params: any

  /**
   * Layer id
   */
  public layerId: string

  /**
   * Constructor
   * @param {Object} params
   */
  constructor(params: any) {
    this.id = Tools.generateUniqueId()

    Tools.configure(this, params)

    this.params = params
  }

  /**
   * Set layer id
   * @param {string} layerId
   */
  public setLayerId(layerId: string) {
    this.layerId = layerId
  }

  /**
   * Get layer id
   * @return {string}
   */
  public getLayerId(): string {
    return this.layerId
  }

  /**
   * Get id
   * @returns {number}
   */
  public getId(): number {
    return this.id
  }

  /**
   * Get x
   * @returns {number}
   */
  public getX(): number {
    return this.x
  }

  /**
   * Get y
   * @returns {number}
   */
  public getY(): number {
    return this.y
  }

  /**
   * Get width
   * @returns {number}
   */
  public getWidth(): number {
    return this.width
  }

  /**
   * Get height
   * @returns {number}
   */
  public getHeight(): number {
    return this.height
  }

  /**
   * Get params
   * @return {any}
   */
  public getParams(): any {
    return this.params
  }

  /**
   * Rendering object
   * @param schema
   * @param view
   */
  public render(schema: Schema, view: View): void {
    this.renderFunction(this, schema, view)
  }

  /**
   * Clear object
   * @param schema
   * @param view
   */
  public clear(schema: Schema, view: View): void {
    this.clearFunction(this, schema, view)
  }

  /**
   * Click on object
   * @param {MouseEvent} e
   * @param {Schema} schemaDesigner
   * @param view
   * @return null|boolean
   */
  public click(e: MouseEvent, schemaDesigner: Schema, view: View): null | void {
    if (typeof this.clickFunction === 'function') {
      return this.clickFunction(this, schemaDesigner, view, e)
    }
    return null
  }

  /**
   * Mouse over
   * @param {MouseEvent} e
   * @param {Schema} schemaDesigner
   * @param view
   * @return null|boolean
   */
  public mouseOver(e: MouseEvent | TouchEvent, schemaDesigner: Schema, view: View): null | boolean {
    if (typeof this.mouseOverFunction === 'function') {
      return this.mouseOverFunction(this, schemaDesigner, view, e)
    }
    return null
  }

  /**
   * Mouse leave
   * @param {MouseEvent} e
   * @param {Schema} schemaDesigner
   * @param view
   * @return null|boolean
   */
  public mouseLeave(
    e: MouseEvent | TouchEvent,
    schemaDesigner: Schema,
    view: View
  ): null | boolean {
    if (typeof this.mouseLeaveFunction === 'function') {
      return this.mouseLeaveFunction(this, schemaDesigner, view, e)
    }
    return null
  }

  /**
   * Check point in object
   * @param point
   * @param schemaDesigner
   * @param view
   * @return boolean
   */
  public checkPointInObject(point: Coordinates, schemaDesigner: Schema, view: View): boolean {
    if (typeof this.pointInObjectFunction === 'function') {
      return this.pointInObjectFunction(this, point, schemaDesigner, view)
    }

    return true
  }

  /**
   * Set x
   * @param {number} value
   */
  public setX(value: number): void {
    this.x = value
  }

  /**
   * Set y
   * @param {number} value
   */
  public setY(value: number): void {
    this.y = value
  }

  /**
   * Set width
   * @param {number} value
   */
  public setWidth(value: number): void {
    this.width = value
  }

  /**
   * Set height
   * @param {number} value
   */
  public setHeight(value: number): void {
    this.height = value
  }

  /**
   * Set cursorStyle
   * @param {string} value
   */
  public setCursorStyle(value: string): void {
    this.cursorStyle = value
  }

  /**
   * Set rotation
   * @param {number} value
   */
  public setRotation(value: number): void {
    this.rotation = value
  }

  /**
   * Set renderFunction
   * @param {Function} value
   */
  public setRenderFunction(value: Function): void {
    this.renderFunction = value
  }

  /**
   * Set clickFunction
   * @param {Function} value
   */
  public setClickFunction(value: Function): void {
    this.clickFunction = value
  }

  /**
   * Set clearFunction
   * @param {Function} value
   */
  public setClearFunction(value: Function): void {
    this.clearFunction = value
  }

  /**
   * Set mouseOverFunction
   * @param {Function} value
   */
  public setMouseOverFunction(value: Function): void {
    this.mouseOverFunction = value
  }

  /**
   * Set mouseLeaveFunction
   * @param {Function} value
   */
  public setMouseLeaveFunction(value: Function): void {
    this.mouseLeaveFunction = value
  }

  /**
   * Set pointInObjectFunction
   * @param {Function} value
   */
  public setPointInObjectFunction(value: Function): void {
    this.pointInObjectFunction = value
  }

  /**
   * Bounding rect
   * @returns BoundingRect
   */
  public getBoundingRect(): BoundingRect {
    return {
      left: this.x,
      top: this.y,
      right: this.x + this.width,
      bottom: this.y + this.height
    }
  }

  /**
   * Outer bound rect
   * @returns {BoundingRect}
   */
  public getOuterBoundingRect(): BoundingRect {
    const boundingRect = this.getBoundingRect()

    if (!this.rotation) {
      return boundingRect
    }

    // rotate from center
    const rectCenterX = (boundingRect.left + boundingRect.right) / 2
    const rectCenterY = (boundingRect.top + boundingRect.bottom) / 2

    const axis: Coordinates = { x: rectCenterX, y: rectCenterY }

    const leftTop = Tools.rotatePointByAxis({ x: this.x, y: this.y }, axis, this.rotation)
    const leftBottom = Tools.rotatePointByAxis(
      { x: this.x, y: this.y + this.height },
      axis,
      this.rotation
    )
    const rightTop = Tools.rotatePointByAxis(
      { x: this.x + this.width, y: this.y },
      axis,
      this.rotation
    )
    const rightBottom = Tools.rotatePointByAxis(
      { x: this.x + this.width, y: this.y + this.height },
      axis,
      this.rotation
    )

    return {
      left: Math.min(leftTop.x, leftBottom.x, rightTop.x, rightBottom.x),
      top: Math.min(leftTop.y, leftBottom.y, rightTop.y, rightBottom.y),
      right: Math.max(leftTop.x, leftBottom.x, rightTop.x, rightBottom.x),
      bottom: Math.max(leftTop.y, leftBottom.y, rightTop.y, rightBottom.y)
    }
  }

  /**
   * Get rotation
   * @returns {number}
   */
  public getRotation(): number {
    return this.rotation
  }

  /**
   * Get is active
   * @return {boolean}
   */
  public isActive(): boolean {
    return this.active
  }

  /**
   * Set active
   * @param {boolean} value
   */
  public setActive(value: boolean): void {
    this.active = value
  }
}
